/**
 * VolumeViewport — split into a one-time lifecycle effect (enable element,
 * register listeners, add to tool group) and a content effect that swaps
 * volumes on seriesId change via `setVolumesForViewports`. Drag-and-drop
 * series swap is a content change, not a remount, so the viewport stays
 * registered against a stable id and listeners survive across swaps.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from "jotai";
import { activeMeasurementToolAtom } from "../atoms/measurementAtoms";
import {
  activeSegmentIndexAtom,
  annotationStartTsAtom,
  bumpRadiusAtom,
  contourVersionAtom,
  derivedMeasurementsAtom,
  hasFirstStrokeAtom,
  isDrawingAtom,
  isSegmentationActiveAtom,
  lockedSeriesIdAtom,
  nnInteractiveLoadingAtom,
  segmentationVolumeIdAtom,
} from "../atoms/segmentationAtoms";
import { setBumpRadius as setBumpRadiusModule } from "../tools/BumpContourTool";
import {
  setRegionGrowAutoSwitchCallback,
  setRegionGrowCommitCallback,
} from "../tools/RegionGrowTool";
import {
  initializeCornerstone,
  getCornerstone,
  getCornerstoneTools,
  getEnums,
} from "../utils/dicomLoader";
import {
  SHARED_RENDERING_ENGINE_ID,
  getOrCreateSharedRenderingEngine,
  disableSharedViewport,
} from "../utils/renderingEngine";
import { on as viewerBusOn, off as viewerBusOff } from "../lib/viewerBus";
import {
  buildS3ImageIds,
  registerImagePlaneMetadataForSeries,
  registerImagePixelMetadataForSeries,
  registerGeneralSeriesMetadataForSeries,
  registerVoiLutMetadataForSeries,
} from "../utils/dicomMetadataLoader";
import { isLoadedFromMask } from "../utils/annotationFlags";
import { volumeAttachQueue } from "../utils/serialVolumeAttach";
import { ensureVolumeAttached } from "../utils/ensureVolumeAttached";
import { StudyMetadataResponse } from "../utils/dicomMetadataLoader";
import { derivePlane } from "../utils/seriesClassification";
import { PLANE_COLORS_HEX } from "../constants/planeColors";
import { currentSliceAtom, totalSlicesAtom } from "../atoms/selectionAtoms";
import { useSegmentationCursor } from "../hooks/useSegmentationCursor";
import { useMeasureClickWiring } from "../hooks/useMeasureClickWiring";
import { SegmentationBadge } from "./SegmentationBadge";
import {
  configureToolGroup,
  configureReadonlyToolGroup,
  ensureContourSegmentationActive,
  getContourSegmentationId,
  switchActiveTool,
} from "../utils/viewportToolSetup";
import { measureModeAtom, measureSessionAtom } from "../atoms/measureAtoms";
import {
  resizeAndRestoreViewports,
  type JumpToSlice,
  type ResizableViewport,
} from "../utils/viewportResize";
import { logger } from "@/utils/logger";
import { snapViewportCameraToCardinal } from "../viewport/cameraSnap";
import {
  applyRadiologicalFlip,
  paneNeedsRadiologicalFlip,
  type FlippableViewport,
} from "../viewport/radiologicalOrientation";
import { aiJumpTargetAtom, type AiJumpTarget } from "../atoms/positionAtoms";
import { kStepToListIndex } from "./volumeViewport.kStepToListIndex";
import { checkAiJumpForGate } from "./aiJumpPlaneGate";
import { sliceWorldPositionFromMetadata } from "./volumeViewport.sliceWorldPosition";
import { resolveAiJumpWorld } from "./aiJumpWorld";

/**
 * Minimum frames needed before showing viewport
 * Show first frame immediately - Cornerstone handles the rest
 */
const VIEWPORT_READY_THRESHOLD = 1;

// How long to wait for a WebGL context to be restored before escalating the
// transient "restoring" state to the actionable reload prompt. vtk-driven
// restoration is near-instant when the browser honors it; a real GPU reset that
// won't recover leaves the user on the reload message after this window.
const RECOVERY_FALLBACK_MS = 8000;

type CsViewport = {
  getImageIds: () => string[];
  getCurrentImageId?: () => string | undefined;
  jumpToWorld?: (ipp: readonly [number, number, number]) => void;
};

function lookupCornerstoneViewport(
  engine: RenderingEngineInstance | null,
  viewportId: string | null,
): CsViewport | null {
  if (!engine || !viewportId) return null;
  const vp = engine.getViewport(viewportId);
  return (vp as unknown as CsViewport) ?? null;
}

// AI-jump plane gate lives in ./aiJumpPlaneGate (pure + unit-tested). Both the
// React effect (atom→effect path) and the volume bind-site (post-bind sync for
// targets that landed during binding) call it and emit this same bail warn id.
function logAiJumpBail(
  reason: "cross_series_no_inslice_point",
  ctx: "effect" | "bind",
  ai: AiJumpTarget,
  seriesId: string,
): void {
  logger.warn(
    "ai_slice_jump_cross %s seriesId=%s targetSeriesId=%s ctx=%s",
    reason,
    seriesId,
    ai.seriesId,
    ctx,
  );
}

interface VolumeViewportProps {
  seriesId: string;
  studyMetadata: StudyMetadataResponse;
  studyId: string;
  onSliceChange?: (slice: number, total: number) => void;
  windowCenter?: number;
  windowWidth?: number;
  onWindowChange?: (center: number, width: number) => void;
  /** External slice control - when provided, viewport navigates to this slice */
  externalSliceIndex?: number;
  /** Custom viewport ID for synchronization (overrides auto-generated ID) */
  viewportIdOverride?: string;
  /** Whether to use global Jotai atoms for slice state (default: true for backward compatibility) */
  useGlobalSliceState?: boolean;
  /** Callback when zoom changes */
  onZoomChange?: (zoom: number) => void;
  /** Fires with current zoom + pan on every CAMERA_MODIFIED. Replaces
   *  onZoomChange as the camera-state mirror. Kept alongside onZoomChange
   *  for backwards compatibility; both fire. */
  onCameraChange?: (zoom: number, pan: [number, number]) => void;
  /** Callback when loading state changes (for progress indicators) */
  onLoadingStateChange?: (
    isLoading: boolean,
    loadedFrames: number,
    totalFrames: number,
    isViewportDisplayable: boolean,
  ) => void;
  /** Mirrors slot state isInverted into Cornerstone via setProperties({invert}).
   *  Without this prop the toggleInvert atom flips state-side only and the
   *  viewport never inverts. */
  isInverted?: boolean;
  /** Rendering blend mode. "composite" (default) renders a single-slice
   *  axial; "MIP"/"MinIP" render a thick-slab projection. Pair with
   *  slabThicknessMm. Used by the slice_dependent gating mode to render an
   *  axial-anchored MIP coplanar viewport next to the primary axial. */
  blendMode?: "composite" | "MIP" | "MinIP";
  /** Slab thickness in millimeters for MIP/MinIP rendering. Ignored when
   *  blendMode is "composite". */
  slabThicknessMm?: number;
  /** "annotate" (default) wires the shared annotation toolgroup and attaches
   *  a segmentation labelmap. "readonly" uses a separate nav-only toolgroup
   *  (StackScroll / WindowLevel / Pan / Zoom) and skips segmentation setup —
   *  appropriate for a sibling MIP viewport that shares the volume but must
   *  not own its own segmentation state. */
  viewportMode?: "annotate" | "readonly";
  /** When true, the initial jumpToSlice lands on the middle of the volume
   *  instead of slice 0. Use for sag/cor in non-phase layouts where slice 0
   *  is just the edge of the volume. */
  startAtMiddleSlice?: boolean;
  /** When true, suppress this viewport's local ResizeObserver because a
   *  parent (HangingProtocolGrid) coordinates resize across siblings. Avoids
   *  the race where the first per-viewport observer's engine.resize resets
   *  every viewport's camera, then later observers capture post-reset state. */
  coordinatedResize?: boolean;
}

// Type for the rendering engine instance
type RenderingEngineInstance = ReturnType<typeof getOrCreateSharedRenderingEngine>;

/**
 * Apply blend mode + slab thickness to a volume viewport. No-op for the
 * default "composite" blend. Cornerstone's setBlendMode accepts the BlendModes
 * enum value, not a string — we map the public string prop here so callers
 * don't need to import cornerstone enums.
 *
 * Called after `setVolumesForViewports` resolves (viewport must own a volume
 * actor) and again from a prop-change effect if blendMode/slabThicknessMm
 * change while the viewport is mounted.
 */
function applyBlendMode(
  engine: RenderingEngineInstance,
  viewportId: string,
  blendMode: "composite" | "MIP" | "MinIP" | undefined,
  slabThicknessMm: number | undefined,
): void {
  if (!blendMode || blendMode === "composite") return;

  const vp = engine.getViewport(viewportId) as any;
  if (!vp || typeof vp.setBlendMode !== "function") return;

  try {
    const Enums = getEnums();
    const modeEnum =
      blendMode === "MIP"
        ? Enums.BlendModes.MAXIMUM_INTENSITY_BLEND
        : Enums.BlendModes.MINIMUM_INTENSITY_BLEND;
    vp.setBlendMode(modeEnum);
    if (typeof slabThicknessMm === "number" && slabThicknessMm > 0) {
      vp.setSlabThickness(slabThicknessMm);
    }
    vp.render?.();
  } catch (e) {
    logger.error("[VolumeViewport] applyBlendMode failed:", e);
  }
}

const VolumeViewport: React.FC<VolumeViewportProps> = ({
  seriesId,
  studyMetadata,
  studyId,
  onSliceChange,
  windowCenter,
  windowWidth,
  onWindowChange,
  externalSliceIndex,
  viewportIdOverride,
  useGlobalSliceState = true,
  onZoomChange,
  onCameraChange,
  onLoadingStateChange,
  isInverted,
  blendMode = "composite",
  slabThicknessMm,
  viewportMode = "annotate",
  startAtMiddleSlice = false,
  coordinatedResize = false,
}) => {
  const isReadonly = viewportMode === "readonly";
  const viewportRef = useRef<HTMLDivElement>(null);
  const renderingEngineRef = useRef<RenderingEngineInstance | null>(null);
  const volumeIdRef = useRef<string | null>(null);
  const volumeRef = useRef<any>(null); // Store volume for cancelLoading()
  // True when this viewport created the volume (vs. attached to a sibling's
  // already-cached volume). Only the creator may cancel the load on unmount.
  const ownsVolumeRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null); // For cancelling fetch requests
  const toolGroupRef = useRef<unknown>(null);
  const imageLoadedListenerRef = useRef<((evt: any) => void) | null>(null);
  // Refs for element-based event listeners (for proper cleanup)
  const volumeLoadingCompletedListenerRef = useRef<((evt: any) => void) | null>(null);
  const volumeNewImageListenerRef = useRef<((evt: any) => void) | null>(null);
  const voiModifiedListenerRef = useRef<((evt: any) => void) | null>(null);
  const cameraModifiedListenerRef = useRef<((evt: any) => void) | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  // Track which individual frames are loaded for per-slice indicators.
  // Only the setter is consumed (drives the onLoadingStateChange callback).
  const [, setLoadedFrames] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // True while a WebGL context loss is being recovered. The content effect
  // short-circuits when true so a seriesId change mid-loss doesn't attach against
  // a context that's still restoring. Cleared on webglContextRestored (self-heal)
  // — no longer a terminal "reload only" flag.
  const contextLostRef = useRef(false);
  // Fallback timer: if the lost context is never restored, escalate the transient
  // "restoring" message to the actionable reload prompt.
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on webglContextRestored to re-run the content effect and re-attach the
  // volume (GPU textures are dropped with the context, so a re-render alone is
  // not enough — setVolumesForViewports must re-upload).
  const [recoveryEpoch, setRecoveryEpoch] = useState(0);
  // Slice state from Jotai atoms - shared with CaseViewer for finding navigation
  // Only used when useGlobalSliceState is true (default for backward compatibility)
  const [globalCurrentSlice, setGlobalCurrentSlice] = useAtom(currentSliceAtom);
  const [globalTotalSlices, setGlobalTotalSlices] = useAtom(totalSlicesAtom);
  // Local slice state for when not using global atoms
  const [localCurrentSlice, setLocalCurrentSlice] = useState(0);
  const [localTotalSlices, setLocalTotalSlices] = useState(0);
  // Use appropriate state based on prop
  const currentSlice = useGlobalSliceState ? globalCurrentSlice : localCurrentSlice;
  const setCurrentSlice = useGlobalSliceState ? setGlobalCurrentSlice : setLocalCurrentSlice;
  const totalSlices = useGlobalSliceState ? globalTotalSlices : localTotalSlices;
  const setTotalSlices = useGlobalSliceState ? setGlobalTotalSlices : setLocalTotalSlices;
  // Track last applied window values to avoid redundant updates from prop changes
  const lastAppliedWindowRef = useRef<{ center: number; width: number } | null>(null);
  // Track the last slice we navigated to (to detect external changes)
  const lastNavigatedSliceRef = useRef<number>(-1); // Start at -1 so initial navigation works
  // Active measurement tool mode from global state
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  // Segmentation atom setters
  const setSegmentationVolumeId = useSetAtom(segmentationVolumeIdAtom);
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  const setIsDrawing = useSetAtom(isDrawingAtom);
  const setAnnotationStartTs = useSetAtom(annotationStartTsAtom);
  const setDerivedMeasurements = useSetAtom(derivedMeasurementsAtom);
  const setHasFirstStroke = useSetAtom(hasFirstStrokeAtom);
  const setLockedSeriesId = useSetAtom(lockedSeriesIdAtom);
  const setActiveMeasurementTool = useSetAtom(activeMeasurementToolAtom);
  const setContourVersion = useSetAtom(contourVersionAtom);
  const activeSegmentIndex = useAtomValue(activeSegmentIndexAtom);
  // Ref-backed mirror so annotation event handlers can gate without
  // re-binding the listener every time the atom flips.
  const isSegmentationActive = useAtomValue(isSegmentationActiveAtom);
  const isSegmentationActiveRef = useRef(isSegmentationActive);
  useEffect(() => {
    isSegmentationActiveRef.current = isSegmentationActive;
  }, [isSegmentationActive]);
  // Tracks the current segmentation id so the modification listener can
  // filter events to ours; updated by the content effect on each swap.
  const segmentationIdRef = useRef<string | null>(null);
  // Deferred segmentation setup. `createAndCacheDerivedVolume` reads
  // `volume.rows`/`columns` to size the labelmap; on a cold load those aren't
  // populated until LOADING_COMPLETED. When that happens we stash a retry
  // closure here so the lifecycle effect's LOADING_COMPLETED listener can
  // run it. Cleared on success or when superseded by a new series.
  const pendingSegSetupRef = useRef<(() => Promise<void>) | null>(null);
  // Stable per-mount viewportId. Set by the lifecycle effect; read by every
  // other effect that needs to address this slot's viewport on the shared
  // engine. Stable across series swaps — that's the point of T2.
  const viewportIdRef = useRef<string | null>(null);
  // Current series's imageIds, used by the long-lived CORNERSTONE_IMAGE_LOADED
  // listener to attribute incoming frames to the right volume.
  const imageIdsRef = useRef<string[] | null>(null);
  // Captures the changing callback/value props so long-lived listeners
  // registered in the lifecycle effect always read the latest values without
  // re-binding on every render.
  const propsRef = useRef({
    onSliceChange,
    onWindowChange,
    onZoomChange,
    onCameraChange,
    windowCenter,
    windowWidth,
    blendMode,
    slabThicknessMm,
    startAtMiddleSlice,
    externalSliceIndex,
  });
  propsRef.current = {
    onSliceChange,
    onWindowChange,
    onZoomChange,
    onCameraChange,
    windowCenter,
    windowWidth,
    blendMode,
    slabThicknessMm,
    startAtMiddleSlice,
    externalSliceIndex,
  };
  // True once the lifecycle effect has enabled the viewport, added it to the
  // tool group, and registered listeners. Gates the content effect so it
  // doesn't try to attach a volume before the viewport exists.
  const [lifecycleReady, setLifecycleReady] = useState(false);

  // Compute if viewport has enough frames to be displayable
  const isViewportDisplayable = useMemo(() => {
    const { loaded, total } = loadingProgress;
    if (!total) return false;
    return loaded >= Math.min(VIEWPORT_READY_THRESHOLD, total);
  }, [loadingProgress]);

  // Notify parent of loading state changes (for hanging protocol progress indicators)
  useEffect(() => {
    onLoadingStateChange?.(
      isLoading,
      loadingProgress.loaded,
      loadingProgress.total,
      isViewportDisplayable,
    );
  }, [isLoading, loadingProgress, onLoadingStateChange, isViewportDisplayable]);

  // ─── Lifecycle effect — runs once per mount ──────────────────────────────
  // Initializes cornerstone, enables the viewport on the shared engine with
  // a stable id, adds to the tool group, and registers listeners that read
  // current state through refs. Cleanup removes the viewport from the tool
  // group, removes segmentation representations, and disables the element.
  useEffect(() => {
    let aborted = false;
    let listenersRegistered = false;
    let toolGroupAdded = false;
    const viewportId = viewportIdOverride ?? "volumeViewport_default";
    // Capture upfront so cleanup uses the same value the async body did.
    viewportIdRef.current = viewportId;

    (async () => {
      await initializeCornerstone();
      if (aborted) return;

      const element = viewportRef.current;
      if (!element) return;

      // Wait for the element to have non-zero dimensions before enabling.
      // Cornerstone needs a valid viewport size; a 0×0 WebGL canvas never recovers.
      while (true) {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) break;
        await new Promise((r) => setTimeout(r, 50));
        if (aborted) return;
      }

      const cornerstone = getCornerstone();
      const Enums = getEnums();
      const engine = getOrCreateSharedRenderingEngine();
      renderingEngineRef.current = engine;

      // Idempotent enableElement so StrictMode dev double-mount and sibling
      // remounts during a swap don't trip on "viewport already exists".
      if (!engine.getViewport(viewportId)) {
        engine.enableElement({
          viewportId,
          type: Enums.ViewportType.ORTHOGRAPHIC,
          element,
          defaultOptions: {
            orientation: Enums.OrientationAxis.ACQUISITION,
            background: [0, 0, 0] as [number, number, number],
          },
        });
      }

      const { ToolGroupManager } = getCornerstoneTools();
      // Readonly viewports (e.g. MIP sibling under slice_dependent gating) get
      // their own toolgroup — same shared engine, but a navigation-only tool
      // set so the labeler can't draw on a viewport that lacks segmentation
      // state. Annotating viewports continue to use the shared toolgroup.
      const toolGroupId = isReadonly
        ? `${SHARED_RENDERING_ENGINE_ID}_toolGroup_readonly`
        : `${SHARED_RENDERING_ENGINE_ID}_toolGroup`;
      let toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
      if (!toolGroup) {
        toolGroup = ToolGroupManager.createToolGroup(toolGroupId);
        if (toolGroup) {
          if (isReadonly) {
            configureReadonlyToolGroup(toolGroup);
          } else {
            configureToolGroup(toolGroup);
          }
        }
      }
      if (toolGroup) {
        toolGroup.addViewport(viewportId, SHARED_RENDERING_ENGINE_ID);
        toolGroupRef.current = toolGroup;
        toolGroupAdded = true;
      }

      // Listeners — registered once, read mutable state via refs so closures
      // don't go stale on a series swap.
      const onImageLoaded = (evt: any) => {
        const loadedImageId = evt.detail?.image?.imageId;
        const imageIds = imageIdsRef.current;
        if (!loadedImageId || !imageIds?.includes(loadedImageId)) return;
        const frameIndex = imageIds.indexOf(loadedImageId);
        if (frameIndex < 0) return;
        setLoadedFrames((prev) => {
          const next = new Set(prev);
          next.add(frameIndex);
          setLoadingProgress({ loaded: next.size, total: imageIds.length });
          return next;
        });
      };
      cornerstone.eventTarget.addEventListener("CORNERSTONE_IMAGE_LOADED" as any, onImageLoaded);
      imageLoadedListenerRef.current = onImageLoaded;

      const onVolumeLoadingCompleted = (evt: any) => {
        const eventVolumeId = evt.detail?.volumeId;
        if (eventVolumeId !== volumeIdRef.current) return;
        const { windowCenter, windowWidth } = propsRef.current;
        if (windowCenter !== undefined && windowWidth !== undefined) {
          const vp = engine.getViewport(viewportId) as any;
          if (vp) {
            vp.setProperties(
              {
                voiRange: {
                  lower: windowCenter - windowWidth / 2,
                  upper: windowCenter + windowWidth / 2,
                },
              },
              volumeIdRef.current ?? undefined,
            );
          }
        }
        engine.renderViewports([viewportId]);
        if (imageIdsRef.current?.length === 1) {
          setCurrentSlice(0);
          lastNavigatedSliceRef.current = 0;
          propsRef.current.onSliceChange?.(0, 1);
        }
        // Drain any deferred segmentation setup (cold-load case where
        // createAndCacheDerivedVolume needed real volume dimensions).
        const pending = pendingSegSetupRef.current;
        if (pending) void pending();
      };
      cornerstone.eventTarget.addEventListener(
        "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED" as any,
        onVolumeLoadingCompleted,
      );
      volumeLoadingCompletedListenerRef.current = onVolumeLoadingCompleted;

      const onVolumeNewImage = (evt: any) => {
        const kStepIndex = evt.detail?.imageIndex;
        const eventViewportId = evt.detail?.viewportId;
        if (eventViewportId !== viewportId) return;
        if (kStepIndex === undefined) return;
        // Cornerstone's VOLUME_NEW_IMAGE emits the K-step (the volume's
        // position along the view-plane normal). The slider / scrollbar /
        // chip data all speak imageIds-list index. When the volume's K-step
        // direction runs opposite to imageIds order (set by the canonicalized
        // HTJ2K pipeline), the two differ by N - 1 - i — feeding K-step into
        // React state where list-index is expected makes the slider thumb
        // jump back the moment the user drags, and the chip's slice number
        // never matches its anatomy. Translate here once so every consumer
        // downstream sees list-index.
        const csViewport = lookupCornerstoneViewport(
          renderingEngineRef.current,
          viewportIdRef.current,
        );
        const imageIds = imageIdsRef.current ?? [];
        let resolved = false;
        let listIndex = kStepIndex;
        let currentImageId: string | null = null;
        try {
          currentImageId = csViewport?.getCurrentImageId?.() ?? null;
          const resolution = kStepToListIndex({ kStepIndex, imageIds, currentImageId });
          resolved = resolution.resolved;
          listIndex = resolution.listIndex;
        } catch (err) {
          logger.debug("[VolumeViewport] onVolumeNewImage list-index lookup:", err);
        }
        if (!resolved) {
          // The kStepIndex fallback is the exact mirror-slice path #401 fixed.
          // Two reasons land here:
          //   currentimageid_null — Cornerstone hasn't published an imageId
          //     yet (volume bootstrap right after jumpToWorld). Acting on the
          //     fallback would snap slot.sliceIndex to the wrong listIndex.
          //     The follow-up VOLUME_NEW_IMAGE (post-jumpToWorld camera move)
          //     resolves cleanly and writes the correct slot value.
          //   imageid_not_in_list — currentImageId present but missing from
          //     imageIds (transcoded form, volume-internal id). Unrecoverable
          //     from this event; skip is still safer than a buggy slot value.
          logger.warn(
            "viewer_kstep_fallback_skipped seriesId=%s reason=%s currentImageId=%s kStepIndex=%d listLen=%d",
            seriesId,
            currentImageId ? "imageid_not_in_list" : "currentimageid_null",
            currentImageId ?? "<null>",
            kStepIndex,
            imageIds.length,
          );
          return;
        }
        setCurrentSlice(listIndex);
        lastNavigatedSliceRef.current = listIndex;
        propsRef.current.onSliceChange?.(listIndex, imageIds.length);
      };
      // Capture phase: cornerstone dispatches this event on a child
      // .viewport-element div, and on the outer element we'd miss it without
      // capture. Fixes wheel-driven slice changes not updating the scrollbar.
      element.addEventListener("CORNERSTONE_VOLUME_NEW_IMAGE" as any, onVolumeNewImage, true);
      volumeNewImageListenerRef.current = onVolumeNewImage;

      const onVoiModified = (evt: any) => {
        const voiRange = evt.detail?.range;
        if (!voiRange) return;
        const center = Math.round((voiRange.lower + voiRange.upper) / 2);
        const width = Math.round(voiRange.upper - voiRange.lower);
        lastAppliedWindowRef.current = { center, width };
        propsRef.current.onWindowChange?.(center, width);
      };
      element.addEventListener("CORNERSTONE_VOI_MODIFIED" as any, onVoiModified);
      voiModifiedListenerRef.current = onVoiModified;

      const onCameraModified = () => {
        const vp = engine.getViewport(viewportId) as any;
        if (!vp) return;
        const zoom = typeof vp.getZoom === "function" ? vp.getZoom() : 1;
        const panRaw = typeof vp.getPan === "function" ? (vp.getPan() as [number, number]) : [0, 0];
        // getPan may return undefined on viewport types that don't expose it.
        const pan: [number, number] = Array.isArray(panRaw)
          ? [panRaw[0] ?? 0, panRaw[1] ?? 0]
          : [0, 0];
        propsRef.current.onZoomChange?.(zoom);
        propsRef.current.onCameraChange?.(zoom, pan);
      };
      element.addEventListener("CORNERSTONE_CAMERA_MODIFIED" as any, onCameraModified);
      cameraModifiedListenerRef.current = onCameraModified;

      listenersRegistered = true;
      if (aborted) return;
      setLifecycleReady(true);
    })().catch((err) => {
      // Without this catch the async IIFE's rejection becomes an unhandled
      // promise — React's ErrorBoundary doesn't see it, lifecycleReady stays
      // false, the content effect's `if (!lifecycleReady) return` bails, and
      // the user sees a black <div> with no error UI. Stable id so this is
      // searchable in CloudWatch when it fires.
      if (aborted) return;
      logger.error(
        "[VolumeViewport] viewer_volume_lifecycle_init_failed seriesId=%s err=%o",
        seriesId,
        err,
      );
      setError("Viewer failed to initialize. Reload the page to retry.");
    });

    return () => {
      aborted = true;
      setLifecycleReady(false);

      const element = viewportRef.current;
      const toolGroup = toolGroupRef.current as any;

      if (ownsVolumeRef.current && volumeRef.current?.cancelLoading) {
        try {
          volumeRef.current.cancelLoading();
        } catch {
          /* already cancelled */
        }
      }
      volumeRef.current = null;
      ownsVolumeRef.current = false;

      if (element && listenersRegistered) {
        if (volumeNewImageListenerRef.current) {
          // Must match the capture-phase listener registered in setup.
          element.removeEventListener(
            "CORNERSTONE_VOLUME_NEW_IMAGE" as any,
            volumeNewImageListenerRef.current,
            true,
          );
        }
        if (voiModifiedListenerRef.current) {
          element.removeEventListener(
            "CORNERSTONE_VOI_MODIFIED" as any,
            voiModifiedListenerRef.current,
          );
        }
        if (cameraModifiedListenerRef.current) {
          element.removeEventListener(
            "CORNERSTONE_CAMERA_MODIFIED" as any,
            cameraModifiedListenerRef.current,
          );
        }
      }

      if (listenersRegistered) {
        try {
          const cornerstone = getCornerstone();
          if (imageLoadedListenerRef.current) {
            cornerstone.eventTarget.removeEventListener(
              "CORNERSTONE_IMAGE_LOADED" as any,
              imageLoadedListenerRef.current,
            );
          }
          if (volumeLoadingCompletedListenerRef.current) {
            cornerstone.eventTarget.removeEventListener(
              "CORNERSTONE_IMAGE_VOLUME_LOADING_COMPLETED" as any,
              volumeLoadingCompletedListenerRef.current,
            );
          }
        } catch {
          /* cornerstone not initialized */
        }
      }

      // Remove segmentation representations on this viewport so we don't
      // leak refs against a viewportId that's about to be disabled.
      try {
        const { segmentation } = getCornerstoneTools();
        // Empty specifier removes all representations on this viewport.
        segmentation.removeSegmentationRepresentations(viewportId, {});
      } catch (e) {
        logger.debug("[VolumeViewport] removeSegmentationRepresentations:", e);
      }

      // Detach from tool group so subsequent mounts don't accumulate stale ids.
      if (toolGroupAdded && toolGroup) {
        try {
          toolGroup.removeViewport(viewportId, SHARED_RENDERING_ENGINE_ID);
        } catch (e) {
          logger.debug("[VolumeViewport] toolGroup.removeViewport:", e);
        }
      }

      disableSharedViewport(viewportId);

      renderingEngineRef.current = null;
      viewportIdRef.current = null;
      toolGroupRef.current = null;
      imageLoadedListenerRef.current = null;
      volumeLoadingCompletedListenerRef.current = null;
      volumeNewImageListenerRef.current = null;
      voiModifiedListenerRef.current = null;
      cameraModifiedListenerRef.current = null;
      segmentationIdRef.current = null;
      volumeIdRef.current = null;
      imageIdsRef.current = null;
      lastNavigatedSliceRef.current = -1;
      lastAppliedWindowRef.current = null;
    };
    // viewportIdOverride is stable per parent component instance; treating it
    // as a mount-time constant matches how parents use it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── WebGL context loss — recover in place (self-heal) ───────────────────
  // The shared rendering engine keeps its canvas alive and requests restoration
  // (preventDefault) instead of destroying itself, so a lost context now heals:
  // on `webglContextLost` we show a TRANSIENT "restoring" state and gate the
  // content effect; on `webglContextRestored` vtk has rebuilt the render window,
  // so we clear the state and bump recoveryEpoch to re-run the content effect and
  // re-upload the volume texture. A fallback timer escalates to the reload prompt
  // only if restoration never arrives (e.g. a real GPU reset the browser won't
  // recover from).
  useEffect(() => {
    const clearTimer = () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
    };
    const onLost = viewerBusOn("webglContextLost", () => {
      contextLostRef.current = true;
      setError("Restoring GPU context…");
      clearTimer();
      recoveryTimerRef.current = setTimeout(() => {
        if (contextLostRef.current) {
          setError("Viewer lost its GPU context. Reload the page to continue.");
        }
      }, RECOVERY_FALLBACK_MS);
    });
    const onRestored = viewerBusOn("webglContextRestored", () => {
      clearTimer();
      contextLostRef.current = false;
      setError(null);
      setRecoveryEpoch((n) => n + 1);
    });
    return () => {
      viewerBusOff("webglContextLost", onLost);
      viewerBusOff("webglContextRestored", onRestored);
      clearTimer();
    };
  }, []);

  // ─── Content effect — swap volumes on seriesId / study change ────────────
  // No teardown of the viewport, listeners, or tool group. The previous
  // volume stays visible until `setVolumesForViewports` resolves with the
  // new one (no gray flash for cached volumes).
  useEffect(() => {
    if (!lifecycleReady) return;
    // After GPU context loss the engine has been destroyed. A new seriesId
    // arriving here would clear the "lost GPU context" error and immediately
    // throw inside the volume attach path — leaving a less actionable message.
    // Hold the original error until the user reloads.
    if (contextLostRef.current) return;
    const engine = renderingEngineRef.current;
    const viewportId = viewportIdRef.current;
    if (!engine || !viewportId) return;

    if (!studyMetadata?.series?.[seriesId]) {
      setError(`Series not available: ${seriesId}`);
      setIsLoading(false);
      return;
    }
    if (!studyMetadata.canonical_cdn_base_url) {
      setError("No canonical CDN base URL configured");
      setIsLoading(false);
      return;
    }

    const instances = studyMetadata.series[seriesId];
    const imageIds = buildS3ImageIds(studyMetadata.canonical_cdn_base_url, instances);
    if (!imageIds.length) {
      setError("No images found in series");
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = abortController;
    if (ownsVolumeRef.current && volumeRef.current?.cancelLoading) {
      try {
        volumeRef.current.cancelLoading();
      } catch (e) {
        logger.debug("[VolumeViewport] cancelLoading (already cancelled):", e);
      }
    }

    setError(null);
    setIsLoading(true);
    setTotalSlices(imageIds.length);
    setLoadingProgress({ loaded: 0, total: imageIds.length });
    setLoadedFrames(new Set());

    imageIdsRef.current = imageIds;
    // Seed every metadata module makeVolumeMetadata reads — imagePixelModule,
    // generalSeriesModule, and imagePlaneModule are destructured without
    // null-check and throw inside a hook body when missing (React surfaces as
    // "Rendered fewer hooks than expected"). voiLutModule is optional but
    // worth populating so the viewport opens at the configured W/L instead
    // of cornerstone's min/max auto-pick. The dicomMetadataLoader.ts module
    // documents the underlying failure mode each register prevents.
    const seriesMeta = studyMetadata.series_metadata?.[seriesId];
    registerImagePlaneMetadataForSeries(
      studyMetadata.canonical_cdn_base_url,
      instances,
      seriesMeta,
    );
    registerImagePixelMetadataForSeries(studyMetadata.canonical_cdn_base_url, instances);
    registerGeneralSeriesMetadataForSeries(
      studyMetadata.canonical_cdn_base_url,
      instances,
      seriesMeta,
    );
    registerVoiLutMetadataForSeries(studyMetadata.canonical_cdn_base_url, instances, seriesMeta);
    const volumeId = `cornerstoneStreamingImageVolume:${studyId}_${seriesId}`;
    volumeIdRef.current = volumeId;

    (async () => {
      try {
        const cornerstone = getCornerstone();

        // Get or create volume. Cached volumes are shared across sibling MPR
        // viewports so we don't double-fetch.
        let volume = cornerstone.cache.getVolume(volumeId);
        let owns = false;
        if (!volume) {
          volume = await cornerstone.volumeLoader.createAndCacheVolume(volumeId, { imageIds });
          if (signal.aborted) return;
          owns = true;
          volume.load();
        }
        volumeRef.current = volume;
        ownsVolumeRef.current = owns;

        // Seed already-cached frames — viewports that attach late (sibling MPR,
        // post-thumbnail-prefetch) miss the load events that fired before
        // mount and would otherwise stay stuck below loaded === total forever,
        // leaving volumeStatus on "loading" and gating off the overlay/seg rep.
        const cachedFrames = (volume as any).cachedFrames as Array<unknown> | undefined;
        if (cachedFrames) {
          const seeded = new Set<number>();
          for (let i = 0; i < cachedFrames.length; i++) {
            if (cachedFrames[i]) seeded.add(i);
          }
          if (seeded.size > 0) {
            setLoadedFrames(seeded);
            setLoadingProgress({ loaded: seeded.size, total: imageIds.length });
          }
        }
        if ((volume as any).loadStatus?.loaded === true) {
          setLoadingProgress({ loaded: imageIds.length, total: imageIds.length });
          setLoadedFrames(new Set(imageIds.map((_, i) => i)));
        }

        // The Tier 2 primitive: swap the volume on the existing viewport.
        // Fix B: serialize this across panes (with an event-loop yield before
        // each) so the default 4-up view's GPU texture uploads don't stack into
        // one unbroken main-thread block that drops scroll input for seconds.
        // The abort check inside skips the upload if this pane tore down while
        // queued behind a sibling.
        await volumeAttachQueue(async () => {
          if (signal.aborted) return;
          await cornerstone.setVolumesForViewports(engine, [{ volumeId }], [viewportId]);
        });
        if (signal.aborted) return;

        // Apply MIP/MinIP rendering if requested. setBlendMode + setSlabThickness
        // operate on the viewport (not the volume), so they must run after the
        // volume is attached. Default `blendMode: "composite"` leaves the
        // existing single-slice behavior untouched for non-MIP callers.
        applyBlendMode(
          engine,
          viewportId,
          propsRef.current.blendMode,
          propsRef.current.slabThicknessMm,
        );

        // Snap camera to nearest cardinal axes so reference lines on sibling
        // MPRs render straight even when a series is acquired slightly oblique.
        // Series with > ~26° off-axis tilt are left alone (deliberate oblique).
        snapViewportCameraToCardinal(
          engine.getViewport(viewportId) as unknown as Parameters<
            typeof snapViewportCameraToCardinal
          >[0],
        );

        // #coronal-lr: force radiological L/R (patient-left on the viewer's
        // right) for series stored in neurological convention — e.g. case-87's
        // sinus axials with a reversed row cosine — so the axial and its MPR
        // reformats agree on laterality. No-op for already-radiological series
        // and for planes without an in-plane L/R axis (sagittal). Runs after the
        // cardinal snap; the manual flip toggle still composes on top. The
        // legacy path never reslices, so the pane plane IS the series plane.
        applyRadiologicalFlip(
          engine.getViewport(viewportId) as unknown as FlippableViewport,
          paneNeedsRadiologicalFlip({
            nativeIop: seriesMeta?.image_orientation_patient ?? null,
            nativePlane: seriesMeta ? derivePlane(seriesMeta) : null,
          }),
          `VolumeViewport ${viewportId}`,
        );

        const { windowCenter, windowWidth } = propsRef.current;
        if (windowCenter !== undefined && windowWidth !== undefined) {
          const vp = engine.getViewport(viewportId) as any;
          if (vp) {
            vp.setProperties(
              {
                voiRange: {
                  lower: windowCenter - windowWidth / 2,
                  upper: windowCenter + windowWidth / 2,
                },
              },
              volumeId,
            );
            lastAppliedWindowRef.current = { center: windowCenter, width: windowWidth };
          }
        }

        // Segmentation: clean the old representation off this viewport, then
        // attach the new series's segmentation. Reading the previous id from
        // a ref lets us skip cleanup on the very first attach.
        // Readonly viewports (e.g. MIP sibling) skip the whole flow — they
        // share the source volume but must not own their own segmentation.
        const segmentationId = `${studyId}_${seriesId}_segmentation`;
        if (!isReadonly) {
          const prevSegmentationId = segmentationIdRef.current;
          if (prevSegmentationId && prevSegmentationId !== segmentationId) {
            try {
              const { segmentation } = getCornerstoneTools();
              segmentation.removeSegmentationRepresentations(viewportId, {});
            } catch (e) {
              logger.debug("[VolumeViewport] removeSegmentationRepresentations (swap):", e);
            }
          }
          segmentationIdRef.current = segmentationId;
          // Drop any pending retry from the previous series so a stale closure
          // doesn't race with the new setup.
          pendingSegSetupRef.current = null;
        }

        // Try seg setup now; on cold load the source volume's dimensions
        // aren't populated yet (rows/columns undefined) and
        // createAndCacheDerivedVolume throws. Defer those to a retry on
        // LOADING_COMPLETED (wired in the lifecycle effect).
        // Skip entirely on readonly viewports.
        if (isReadonly) {
          if (signal.aborted) return;
          engine.renderViewports([viewportId]);
          setIsLoading(false);
          if (imageIds.length === 1) {
            setCurrentSlice(0);
            lastNavigatedSliceRef.current = 0;
            propsRef.current.onSliceChange?.(0, 1);
          }
          return;
        }

        const runSegmentationSetup = async (): Promise<boolean> => {
          const srcVolume: any = cornerstone.cache.getVolume(volumeId);
          const dimsReady = Array.isArray(srcVolume?.dimensions) && srcVolume.dimensions[0] != null;
          if (!dimsReady) return false;
          const { segmentation, Enums: ToolEnums } = getCornerstoneTools();
          try {
            await segmentation.removeSegmentation(segmentationId);
          } catch (e) {
            logger.debug("[VolumeViewport] removeSegmentation (expected if first load):", e);
          }
          if (signal.aborted) return true;
          cornerstone.volumeLoader.createAndCacheDerivedVolume(volumeId, {
            volumeId: segmentationId,
          });
          segmentation.addSegmentations([
            {
              segmentationId,
              representation: {
                type: ToolEnums.SegmentationRepresentations.Labelmap,
                data: { volumeId: segmentationId },
              },
            },
          ]);
          segmentation.addSegmentationRepresentations(viewportId, [
            {
              segmentationId,
              type: ToolEnums.SegmentationRepresentations.Labelmap,
            },
          ]);
          setSegmentationVolumeId(segmentationId);
          return true;
        };

        let segSetupDone = false;
        try {
          segSetupDone = await runSegmentationSetup();
        } catch (e) {
          logger.error("[VolumeViewport] Segmentation setup failed:", e);
        }
        if (!segSetupDone) {
          // Cold-load case: dimensions not yet hydrated. Stash the retry for
          // the lifecycle effect's LOADING_COMPLETED listener to fire.
          pendingSegSetupRef.current = async () => {
            try {
              const ok = await runSegmentationSetup();
              if (ok) pendingSegSetupRef.current = null;
            } catch (e) {
              logger.error("[VolumeViewport] Deferred segmentation setup failed:", e);
            }
          };
        }

        if (signal.aborted) return;
        engine.renderViewports([viewportId]);
        // VOLUME_NEW_IMAGE may not refire when attaching to an already-cached
        setIsLoading(false);

        // Initial slice + first-render sync to Cornerstone. With
        // setVolumesForViewports resolved the viewport has a volume actor
        // and jumpToWorld is safe. Drive Cornerstone imperatively here
        // (bind site owns the post-bind sync); the atom-driven nav
        // effects below handle subsequent changes only — they don't have
        // to retry across a "ready" flag flip.
        //
        // Slice selection: useGlobalSliceState reads the atom directly,
        // so a strip-card jump that mutated the atom while the volume
        // was still streaming is picked up here on bind completion.
        // Local-state path uses externalSliceIndex prop, with the
        // startAtMiddleSlice fallback for sag/cor (edges are blank).
        let initialIndex: number;
        if (imageIds.length === 1) {
          initialIndex = 0;
          setCurrentSlice(0);
          propsRef.current.onSliceChange?.(0, 1);
        } else if (useGlobalSliceState) {
          initialIndex = getDefaultStore().get(currentSliceAtom);
        } else {
          initialIndex =
            propsRef.current.externalSliceIndex ??
            (propsRef.current.startAtMiddleSlice ? Math.floor(imageIds.length / 2) : 0);
          propsRef.current.onSliceChange?.(initialIndex, imageIds.length);
        }
        const initialIpp = sliceWorldPositionFromMetadata(
          cornerstone.metaData,
          imageIds[initialIndex],
        );
        const csVp = engine.getViewport(viewportId) as unknown as {
          jumpToWorld?: (worldPos: [number, number, number]) => void;
        } | null;
        if (initialIpp && typeof csVp?.jumpToWorld === "function") {
          try {
            csVp.jumpToWorld(initialIpp);
            // Listener (sync inside jumpToWorld) writes the actual landed
            // list-index to `lastNavigatedSliceRef`; mirroring initialIndex
            // here would clobber that on reversed-order recon planes — #468.
          } catch (err) {
            logger.warn("[VolumeViewport] initial jumpToWorld failed: %o", err);
          }
        }

        // Bind-site sync of any outstanding AI jump. The aiJumpTarget
        // React effect may have fired while the volume was still
        // binding — in that case it would have caught a no-volume-actor
        // throw and intentionally left `lastFiredAiJumpTriggerRef`
        // unset (see the catch above). Catch the unfired target here.
        // Shares the same FoR gate + warn ids as the React effect so
        // FoR-mismatched targets land in ops logs whichever path fires
        // first.
        const ai = getDefaultStore().get(aiJumpTargetAtom);
        if (
          ai &&
          lastFiredAiJumpTriggerRef.current !== ai.trigger &&
          csVp &&
          typeof csVp.jumpToWorld === "function"
        ) {
          const gate = checkAiJumpForGate(ai, seriesId);
          if (!gate.ok) {
            logAiJumpBail(gate.reason, "bind", ai, seriesId);
            lastFiredAiJumpTriggerRef.current = ai.trigger;
          } else {
            try {
              // Same real-geometry preference as the React-effect site (#921).
              csVp.jumpToWorld(resolveAiJumpWorld(ai, cornerstone.metaData, imageIds).world);
              // Listener owns `lastNavigatedSliceRef`. Writing ai.sliceIndex
              // here clobbers the actual landed list-index on reversed-order
              // recon planes — #468.
              lastFiredAiJumpTriggerRef.current = ai.trigger;
              logger.info(
                "ai_slice_jump_bind_sync seriesId=%s targetSeriesId=%s sliceIndex=%d trigger=%d",
                seriesId,
                ai.seriesId,
                ai.sliceIndex,
                ai.trigger,
              );
            } catch (err) {
              logger.warn(
                "ai_slice_jump_cross jumpToWorld failed seriesId=%s targetSeriesId=%s ctx=bind err=%s",
                seriesId,
                ai.seriesId,
                err instanceof Error ? err.message : String(err),
              );
            }
          }
        }
      } catch (err) {
        if (signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to attach volume");
        setIsLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [
    lifecycleReady,
    seriesId,
    studyId,
    studyMetadata,
    setCurrentSlice,
    setTotalSlices,
    setSegmentationVolumeId,
    // Re-attach the volume after a WebGL context loss/restore (textures dropped).
    recoveryEpoch,
  ]);

  // Handle container resize - notify Cornerstone to resize viewport.
  // Skipped when a parent (HangingProtocolGrid) is coordinating resize
  // across siblings; that path captures all presentations before a single
  // engine.resize, avoiding the race where the first per-viewport observer
  // resets every sibling's camera before the others can capture pre-resize
  // state.
  //
  // Note: this path doesn't gate on `lifecycleReady` — the helper safely
  // handles a viewport whose getViewPresentation/getSliceIndex returns
  // null (pre-image-attach), and `engine.getViewport()` returning undefined
  // short-circuits the call before any resize work runs.
  useEffect(() => {
    if (coordinatedResize) return;
    const element = viewportRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const engine = renderingEngineRef.current;
      const viewportId = viewportIdRef.current;
      if (!engine || !viewportId) return;

      // Single-element observer — only entries[0] matters.
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width <= 0 || height <= 0) return;

      const vp = engine.getViewport(viewportId) as ResizableViewport | undefined;
      if (!vp) return;

      let jumpToSlice: JumpToSlice;
      try {
        jumpToSlice = getCornerstone().utilities.jumpToSlice as JumpToSlice;
      } catch {
        return;
      }

      resizeAndRestoreViewports(engine, [vp], {
        jumpToSlice,
        logContext: "VolumeViewport",
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [coordinatedResize]);

  // Update windowing when props change (e.g., from preset buttons)
  // This effect only runs when windowCenter/windowWidth props change from parent
  useEffect(() => {
    if (!renderingEngineRef.current || isLoading) return;
    if (!viewportIdRef.current) return;
    if (windowCenter === undefined || windowWidth === undefined) return;

    // Skip if we already applied these exact values (prevents feedback loops)
    if (
      lastAppliedWindowRef.current &&
      lastAppliedWindowRef.current.center === windowCenter &&
      lastAppliedWindowRef.current.width === windowWidth
    ) {
      return;
    }

    const viewport = renderingEngineRef.current.getViewport(viewportIdRef.current) as unknown as {
      setProperties: (
        props: { voiRange?: { lower: number; upper: number } },
        volumeId?: string,
      ) => void;
      render: () => void;
    } | null;
    if (viewport && volumeIdRef.current) {
      viewport.setProperties(
        {
          voiRange: {
            lower: windowCenter - windowWidth / 2,
            upper: windowCenter + windowWidth / 2,
          },
        },
        volumeIdRef.current,
      );
      viewport.render();
      // Track what we applied
      lastAppliedWindowRef.current = { center: windowCenter, width: windowWidth };
    }
  }, [windowCenter, windowWidth, isLoading]);

  // Switch measurement tools based on global atom state. When measureMode is
  // active (📐 click-to-measure flow), force the MeasureClickTool onto the
  // primary mouse button until the first click resolves; afterwards (a
  // session exists) hand primary back to the user's tool and listen for
  // refinement only on shift/ctrl/meta.
  const measureMode = useAtomValue(measureModeAtom);
  const measureSession = useAtomValue(measureSessionAtom);
  const measureRefining = !!(measureMode && measureSession);
  const effectiveTool = measureMode && !measureRefining ? "measure" : activeMeasurementTool;

  // Sync the bump-radius atom to the BumpContourTool. The radius is consumed
  // both via `toolGroup.setToolConfiguration("BumpContourTool", { radius })`
  // (primary path the tool reads on mouse-down) and a module-level fallback in
  // BumpContourTool.ts. Without this wire-up the UI buttons update the atom
  // but the brush size never changes.
  const bumpRadius = useAtomValue(bumpRadiusAtom);
  useEffect(() => {
    setBumpRadiusModule(bumpRadius);
    const tg = toolGroupRef.current as any;
    if (!tg || !lifecycleReady) return;
    try {
      tg.setToolConfiguration?.("BumpContourTool", { radius: bumpRadius });
    } catch (e) {
      logger.debug("[VolumeViewport] bump radius setToolConfiguration skipped:", e);
    }
  }, [bumpRadius, lifecycleReady]);

  // Ensure a Contour-typed segmentation rep is registered and active on this
  // viewport once the labelmap exists, and re-apply the active-segment color
  // every time activeSegmentIndex changes. Owning this in VolumeViewport
  // (rather than SegmentationAnnotationPanel) keeps every cornerstone-state side
  // effect in one place per the stack→volume migration plan.
  useEffect(() => {
    if (!segmentationVolumeId || !lifecycleReady) return;
    ensureContourSegmentationActive(segmentationVolumeId, activeSegmentIndex);
  }, [segmentationVolumeId, activeSegmentIndex, lifecycleReady]);

  // Region-grow commit + auto-switch callbacks. Flip drawing-state gating
  // when a seed click lands (Save button enables, series locks, HU overlay
  // populates).
  //
  // Keyed by `viewportId` so a 3-plane hanging protocol (axial + coronal +
  // sagittal) dispatches to whichever viewport the click landed on, instead
  // of "last viewport to mount wins". The tool resolves the right entry
  // from `ctx.viewport.id` at fire time.
  //
  // Gated on `segmentationVolumeId` because the seg rep + viewportId both
  // need to be in place before we wire region-grow commit callbacks, and we
  // need the real viewportId to key the Map.
  useEffect(() => {
    const vpId = viewportIdRef.current;
    if (!segmentationVolumeId || !vpId) return;
    setRegionGrowCommitCallback(vpId, (currentImageId: string | null = null) => {
      if (!isSegmentationActiveRef.current) return;
      setIsDrawing(true);
      setHasFirstStroke(true);
      setLockedSeriesId(seriesId);
      setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());

      // Cross-plane companion-viewport sync: jump every viewport to the
      // slice that contains the seed contour's world centroid. Fires once
      // per region-grow click (not per added annotation), so multi-viewport
      // seed propagation doesn't trigger N camera jumps.
      try {
        const cs = getCornerstone();
        const ct = getCornerstoneTools();
        const allAnns: any[] = ct.annotation.state.getAllAnnotations() ?? [];
        const seed = [...allAnns]
          .reverse()
          .find(
            (a: any) =>
              a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
              !a?.autoGenerated &&
              !isLoadedFromMask(a) &&
              (currentImageId ? a?.metadata?.referencedImageId === currentImageId : true) &&
              (a?.data?.contour?.polyline?.length ?? 0) > 0,
          );
        const polyline: number[][] = seed?.data?.contour?.polyline ?? [];
        if (polyline.length === 0) return;
        let cx = 0,
          cy = 0,
          cz = 0;
        for (const p of polyline) {
          cx += p[0];
          cy += p[1];
          cz += p[2];
        }
        const centroid: [number, number, number] = [
          cx / polyline.length,
          cy / polyline.length,
          cz / polyline.length,
        ];
        // Find the primary viewport (one whose imageIds include the seed's
        // referencedImageId) so we can exempt it from the cross-plane jump
        // below — the user just clicked there, so re-jumping would yank the
        // camera off the slice they're looking at.
        const seedRefImageId = seed?.metadata?.referencedImageId;
        const engines = cs.getRenderingEngines?.() ?? [];
        let primaryVp: any = null;
        if (seedRefImageId) {
          for (const engine of engines) {
            for (const vp of engine.getViewports?.() ?? []) {
              const ids: string[] = (vp as any).getImageIds?.() ?? [];
              if (ids.includes(seedRefImageId)) {
                primaryVp = vp;
                break;
              }
            }
            if (primaryVp) break;
          }
        }
        // Per item 7 of the workflow spec: each viewport should land on an
        // annotated slice. Companion viewports that already have committed
        // contours for the current segment are intentionally LEFT in place.
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            if (typeof (vp as any).jumpToWorld !== "function") continue;

            // Never re-jump the primary (clicked) viewport. The user just
            // clicked it, so it's already at the right slice. Under
            // progressive HTJ2K the camera focal Z can drift from the
            // displayed slice index, and jumpToWorld(centroid) would snap
            // the camera to that drift — moving the axial view "to the
            // wrong slice" the moment a seed is committed.
            if (vp === primaryVp) continue;

            const vpImageIds: string[] = (vp as any).getImageIds?.() ?? [];
            const vpImageIdSet = new Set(vpImageIds);
            const hasOtherContour = allAnns.some(
              (a: any) =>
                a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
                a?.data?.segmentation?.segmentIndex === activeSegmentIndex &&
                a?.metadata?.referencedImageId &&
                vpImageIdSet.has(a.metadata.referencedImageId) &&
                a?.annotationUID !== seed?.annotationUID,
            );
            if (hasOtherContour) continue;

            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[VolumeViewport] commit jumpToWorld skipped:", err);
            }
          }
        }
      } catch (err) {
        logger.debug("[VolumeViewport] commit cross-plane nav failed:", err);
      }
    });
    setRegionGrowAutoSwitchCallback(vpId, () => {
      if (!isSegmentationActiveRef.current) return;
      setActiveMeasurementTool("bump");
    });
    return () => {
      setRegionGrowCommitCallback(vpId, null);
      setRegionGrowAutoSwitchCallback(vpId, null);
    };
  }, [
    segmentationVolumeId,
    seriesId,
    setIsDrawing,
    setHasFirstStroke,
    setLockedSeriesId,
    setAnnotationStartTs,
    setActiveMeasurementTool,
  ]);

  useEffect(() => {
    if (!toolGroupRef.current || !lifecycleReady) return;
    // #834: an aborted mid-attach can leave this viewport live-but-actor-less,
    // which makes the ruler (LengthTool) silently no-op because it has no target
    // volume. Heal that end-state before activating the tool, so selecting the
    // ruler on the AI/priority (soft-tissue) series measures instead of failing
    // while the plain bone series works. No-op once the actor is present.
    const engine = renderingEngineRef.current;
    const viewportId = viewportIdRef.current;
    const volumeId = volumeIdRef.current;
    if (engine && viewportId && volumeId) {
      try {
        const cornerstone = getCornerstone();
        void ensureVolumeAttached(
          {
            getViewport: (id) =>
              engine.getViewport(id) as unknown as { getActors?: () => unknown[] },
            setVolumes: (inputs, ids) => cornerstone.setVolumesForViewports(engine, inputs, ids),
            logger,
          },
          viewportId,
          volumeId,
        ).then((healed) => {
          if (healed) engine.renderViewports([viewportId]);
        });
      } catch (e) {
        logger.warn("volume_attach_recovery_skipped", {
          reason: "cornerstone_not_ready",
          error: String(e),
        });
      }
    }
    switchActiveTool(toolGroupRef.current, effectiveTool, {
      measureRefinement: measureRefining,
    });
  }, [effectiveTool, measureRefining, lifecycleReady]);

  // Listen for segmentation changes to derive measurements and record start_ts
  useEffect(() => {
    // Guard: Cornerstone may not be initialized yet if this effect runs before
    // setupVolumeViewport completes (e.g. on first mount or activeSegmentIndex change).
    let eventTarget: ReturnType<typeof getCornerstone>["eventTarget"];
    try {
      ({ eventTarget } = getCornerstone());
    } catch {
      return; // Will re-run when activeSegmentIndex changes after initialization
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const onSegmentationModified = (evt: any) => {
      const segId = evt.detail?.segmentationId;
      const labelmapId = segmentationIdRef.current;
      if (!segId || !labelmapId) return;
      // Accept events from either the paired labelmap seg or its contour seg
      // (see getContourSegmentationId). Both are written during a single
      // user stroke and either is sufficient to mark drawing state.
      const contourId = getContourSegmentationId(labelmapId);
      if (segId !== labelmapId && segId !== contourId) return;

      // Drawing-state gate: only flip isDrawing when the user has an
      // annotation form open. Background voxel writes (niftiMaskLoader mask
      // restore on case open) shouldn't disable the Add button.
      //
      // Asymmetry note: when inactive AND the event is for the labelmap
      // (mask restore writes voxels directly, no contour event), we still
      // fall through to the voxel-iteration block below so derived
      // measurements (volume/HU stats) update from the restored mask.
      // Inactive + contour-only events have no measurement payload, so
      // those bail early. Active in either case → flip drawing state.
      if (!isSegmentationActiveRef.current) {
        if (segId !== labelmapId) return;
      } else {
        setIsDrawing(true);
        setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());
      }

      // The voxel-iteration measurement work below only applies to the
      // labelmap volume — skip it for contour-only events.
      if (segId !== labelmapId) return;

      // Debounce the expensive voxel iteration — on large volumes (500+ slices) iterating
      // the entire scalar array on every brush stroke would freeze the UI thread.
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Derive measurements from the labelmap
        try {
          const cornerstone = getCornerstone();
          const volume = cornerstone.cache.getVolume(segId) as any;
          if (!volume) return;

          const { dimensions, spacing } = volume;
          const scalarData: Uint8Array =
            typeof volume.getScalarData === "function" ? volume.getScalarData() : volume.scalarData;
          if (!scalarData) return;

          const [dx, dy, dz] = spacing as [number, number, number];
          const [cols, rows, slices] = dimensions as [number, number, number];
          const voxelVolume = dx * dy * dz;

          let count = 0;
          let huMin = Infinity,
            huMax = -Infinity,
            huSum = 0;
          const sliceSet = new Set<number>();
          let minR = rows,
            maxR = 0,
            minC = cols,
            maxC = 0,
            minS = slices,
            maxS = 0;

          // CT volume for HU values
          const ctVolume = cornerstone.cache.getVolume(volumeIdRef.current ?? "") as any;
          const ctScalar = ctVolume?.getScalarData
            ? ctVolume.getScalarData()
            : ctVolume?.scalarData;
          const sliceSize = cols * rows;

          // Single pass: collect voxel count, HU stats, slice set, and bounding box
          for (let i = 0; i < scalarData.length; i++) {
            if (scalarData[i] === activeSegmentIndex) {
              count++;
              const s = Math.floor(i / sliceSize);
              const rem = i % sliceSize;
              const r = Math.floor(rem / cols);
              const c = rem % cols;
              sliceSet.add(s);
              if (r < minR) minR = r;
              if (r > maxR) maxR = r;
              if (c < minC) minC = c;
              if (c > maxC) maxC = c;
              if (s < minS) minS = s;
              if (s > maxS) maxS = s;
              if (ctScalar) {
                const hu = ctScalar[i] as number;
                if (hu < huMin) huMin = hu;
                if (hu > huMax) huMax = hu;
                huSum += hu;
              }
            }
          }

          if (count === 0) return;

          // Estimate long/short axis from bounding box in mm
          // (full eigenvector decomposition deferred to backend)
          const extX = (maxC - minC) * dx;
          const extY = (maxR - minR) * dy;
          const extZ = (maxS - minS) * dz;
          const extents = [extX, extY, extZ].sort((a, b) => b - a);

          setDerivedMeasurements({
            volume_mm3: count * voxelVolume,
            long_axis_mm: extents[0],
            short_axis_mm: extents[1],
            slice_count: sliceSet.size,
            hu_min: ctScalar ? huMin : 0,
            hu_max: ctScalar ? huMax : 0,
            hu_avg: ctScalar && count > 0 ? huSum / count : 0,
          });
        } catch {
          // Measurement derivation is best-effort; backend recomputes on save
        }
      }, 150);
    };

    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_SEGMENTATION_DATA_MODIFIED" as any,
      onSegmentationModified,
    );

    // SEGMENTATION_DATA_MODIFIED only fires when labelmap voxels change.
    // Pure-contour strokes (lasso, region-grow) don't touch the labelmap
    // without polySeg, so the badge would never appear. Listen for
    // ANNOTATION_ADDED on PlanarFreehandContourSegmentationTool annotations
    // tagged with one of our segmentationIds to flip drawing state on the
    // first contour stroke as well.
    const onAnnotationAdded = (evt: any) => {
      // Hard gate: if no annotation form is open, the user is not drawing —
      // ignore restored/programmatic adds so the "Add Annotation" button
      // doesn't get stuck disabled by background activity.
      if (!isSegmentationActiveRef.current) return;
      const ann = evt.detail?.annotation;
      if (!ann || ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return;
      // Skip programmatic loads (niftiMaskLoader on case open) and
      // region-grow propagation — only user-driven keyframes (lasso strokes,
      // region-grow seed clicks) should flip drawing state.
      if (isLoadedFromMask(ann) || ann.autoGenerated) return;
      const labelmapId = segmentationIdRef.current;
      if (!labelmapId) return;
      const contourId = getContourSegmentationId(labelmapId);
      const annSegId = ann.data?.segmentation?.segmentationId;
      if (annSegId !== labelmapId && annSegId !== contourId) return;
      setIsDrawing(true);
      setAnnotationStartTs((prev: string | null) => prev ?? new Date().toISOString());

      // 3D-viewer refresh + cross-plane jump fire on ANNOTATION_COMPLETED
      // (below), not here. ANNOTATION_ADDED fires at mousedown when the
      // polyline still has 0–1 points, so bumping here would re-derive
      // polylines before the stroke has geometry — and the NEXT bump
      // wouldn't fire until the user begins another stroke (one-stroke
      // render lag).
    };
    eventTarget.addEventListener("CORNERSTONE_TOOLS_ANNOTATION_ADDED" as any, onAnnotationAdded);

    // ANNOTATION_COMPLETED handler: bump contour version + jump companion
    // viewports to the stroke centroid. Same gating as the ADDED handler
    // above, but runs against the finalized polyline at mouseup.
    const onAnnotationCompleted = (evt: any) => {
      if (!isSegmentationActiveRef.current) return;
      const ann = evt.detail?.annotation;
      if (!ann || ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return;
      if (isLoadedFromMask(ann) || ann.autoGenerated) return;
      const labelmapId = segmentationIdRef.current;
      if (!labelmapId) return;
      const contourId = getContourSegmentationId(labelmapId);
      const annSegId = ann.data?.segmentation?.segmentationId;
      if (annSegId !== labelmapId && annSegId !== contourId) return;
      // Region-grow seeds carry `_regionGrow: true` (RegionGrowTool.ts:345).
      // Their cross-plane jump runs from setRegionGrowCommitCallback, so
      // skip the lasso/bump path here to avoid a duplicate jump.
      if ((ann as any)._regionGrow) return;

      const refImageId: string | undefined = ann.metadata?.referencedImageId;
      if (!refImageId) return;

      // Refresh the 3D viewer + stats overlay. SegmentationViewer3D re-derives
      // polylines whenever contourVersion bumps; without this, lasso strokes
      // wouldn't appear in 3D until the user saved or deleted something.
      setContourVersion((v) => v + 1);

      // Cross-plane companion-viewport sync. Mirrors the region-grow commit
      // callback at line ~971: jump companions to the stroke centroid so the
      // sagittal/coronal views land on the lesion. Skip companions that
      // already have a committed contour for this segment, so multi-slice
      // editing doesn't yank the camera away from the user's current focus.
      try {
        const polyline: number[][] = ann.data?.contour?.polyline ?? [];
        if (polyline.length === 0) return;
        let cx = 0,
          cy = 0,
          cz = 0;
        for (const p of polyline) {
          cx += p[0];
          cy += p[1];
          cz += p[2];
        }
        const centroid: [number, number, number] = [
          cx / polyline.length,
          cy / polyline.length,
          cz / polyline.length,
        ];
        const cs = getCornerstone();
        const ct = getCornerstoneTools();
        const allAnns: any[] = ct.annotation.state.getAllAnnotations() ?? [];
        const engines = cs.getRenderingEngines?.() ?? [];

        // Find the "primary" viewport — the one whose imageIds contain the
        // annotation's referencedImageId (this is the viewport the user drew
        // on, not necessarily the viewport whose listener fired). Skip it
        // when jumping; the user just clicked it, so re-jumping would yank
        // the camera off the slice they're working on.
        let primaryVp: any = null;
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            const ids: string[] = (vp as any).getImageIds?.() ?? [];
            if (ids.includes(refImageId)) {
              primaryVp = vp;
              break;
            }
          }
          if (primaryVp) break;
        }

        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            if (vp === primaryVp) continue;
            if (typeof (vp as any).jumpToWorld !== "function") continue;
            const vpImageIds: string[] = (vp as any).getImageIds?.() ?? [];
            const vpImageIdSet = new Set(vpImageIds);
            const hasOtherContour = allAnns.some(
              (a: any) =>
                a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
                a?.data?.segmentation?.segmentIndex === activeSegmentIndex &&
                a?.metadata?.referencedImageId &&
                vpImageIdSet.has(a.metadata.referencedImageId) &&
                a?.annotationUID !== ann.annotationUID,
            );
            if (hasOtherContour) continue;
            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[VolumeViewport] lasso jumpToWorld skipped:", err);
            }
          }
        }
      } catch (err) {
        logger.debug("[VolumeViewport] lasso cross-plane nav failed:", err);
      }
    };
    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED" as any,
      onAnnotationCompleted,
    );

    // Cross-viewport sync on annotation click. Cornerstone fires
    // ANNOTATION_SELECTION_CHANGE when the user clicks a contour in any
    // viewport; we mirror that into a jumpToWorld on every volume viewport so
    // the companion planes snap to the slice containing the lesion's centroid.
    // Wired here (not in CaseViewer) per the stack→volume migration plan that
    // pulls all viewport-side gating into VolumeViewport.
    const onAnnotationSelectionChange = (evt: any) => {
      const added: string[] = evt.detail?.added ?? [];
      if (added.length === 0) return;
      const cornerstone = getCornerstone();
      const cornerstoneTools = getCornerstoneTools();
      const ann = cornerstoneTools.annotation.state.getAnnotation?.(added[0]);
      const polyline: number[][] = ann?.data?.contour?.polyline ?? [];
      if (polyline.length === 0) return;
      let cx = 0,
        cy = 0,
        cz = 0;
      for (const p of polyline) {
        cx += p[0];
        cy += p[1];
        cz += p[2];
      }
      const centroid: [number, number, number] = [
        cx / polyline.length,
        cy / polyline.length,
        cz / polyline.length,
      ];
      // Identify the "primary" viewport — the one whose imageIds include the
      // selected annotation's referencedImageId. We must NOT re-jump it: the
      // user just placed/clicked the seed, so it's already at the right slice,
      // and under progressive HTJ2K the camera focal Z drifts from the
      // displayed slice index. Re-jumping moves the active axial view off
      // the stone the moment region-grow commits. Mirrors the same guard the
      // region-grow commit callback uses (~L1010).
      const selectedRefImageId: string | undefined = ann?.metadata?.referencedImageId;
      const engines = cornerstone.getRenderingEngines?.() ?? [];
      let primaryVp: any = null;
      if (selectedRefImageId) {
        for (const engine of engines) {
          for (const vp of engine.getViewports?.() ?? []) {
            const ids: string[] = (vp as any).getImageIds?.() ?? [];
            if (ids.includes(selectedRefImageId)) {
              primaryVp = vp;
              break;
            }
          }
          if (primaryVp) break;
        }
      }
      for (const engine of engines) {
        for (const vp of engine.getViewports?.() ?? []) {
          if (vp === primaryVp) continue;
          if (typeof (vp as any).jumpToWorld === "function") {
            try {
              (vp as any).jumpToWorld(centroid);
            } catch (err) {
              logger.debug("[VolumeViewport] selection jumpToWorld skipped:", err);
            }
          }
        }
      }
    };
    eventTarget.addEventListener(
      "CORNERSTONE_TOOLS_ANNOTATION_SELECTION_CHANGE" as any,
      onAnnotationSelectionChange,
    );

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_SEGMENTATION_DATA_MODIFIED" as any,
        onSegmentationModified,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_ADDED" as any,
        onAnnotationAdded,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_COMPLETED" as any,
        onAnnotationCompleted,
      );
      eventTarget.removeEventListener(
        "CORNERSTONE_TOOLS_ANNOTATION_SELECTION_CHANGE" as any,
        onAnnotationSelectionChange,
      );
    };
  }, [
    activeSegmentIndex,
    setIsDrawing,
    setAnnotationStartTs,
    setDerivedMeasurements,
    setContourVersion,
  ]);

  // Navigate to slice when currentSlice atom changes externally (e.g., finding click)
  // Only used when useGlobalSliceState is true
  useEffect(() => {
    if (!useGlobalSliceState) return; // Skip if not using global state
    // No volume-bound gate. The bind effect imperatively syncs to the
    // current desired slice once setVolumesForViewports resolves; this
    // effect handles subsequent atom changes. Pre-bind calls are
    // silently no-op'd by the try/catch below — the bind-site sync
    // covers the post-bind state.
    if (!volumeIdRef.current) return;
    if (!viewportRef.current) return;
    if (currentSlice === lastNavigatedSliceRef.current) return; // Already at this slice

    // Bounds check - prevent navigation to invalid slices
    if (totalSlices > 0 && (currentSlice < 0 || currentSlice >= totalSlices)) {
      return;
    }

    const cornerstone = getCornerstone();
    const csViewport = lookupCornerstoneViewport(renderingEngineRef.current, viewportIdRef.current);

    try {
      // Resolve the slice's world position from Cornerstone's own per-imageId
      // imagePlaneModule (the real IPP the volume was built from), keyed by the
      // stable imageId — see sliceWorldPositionFromMetadata. studyMetadata's
      // image_position_patient is absent on canonical studies, and its
      // z_position can run opposite to the real IPP on plane-aware-reversed
      // reformats, so jumpToWorld off either lands on the mirror slice; and
      // jumpToSlice({ imageIndex }) treats the list-index as a K-step (same
      // mirror). #401 fixed the K-step→list read-back; this keeps the
      // write/jump in the same real-geometry frame.
      const ipp = sliceWorldPositionFromMetadata(
        cornerstone.metaData,
        (imageIdsRef.current ?? [])[currentSlice],
      );
      if (ipp && typeof csViewport?.jumpToWorld === "function") {
        csViewport.jumpToWorld(ipp);
      } else {
        // Frame metadata not registered yet — last-resort jumpToSlice (treats
        // the index as a K-step, may mirror on reversed volumes until the
        // frame's IPP loads). utilities.jumpToSlice is async; attach `.catch`
        // so rejections route through logger.warn instead of leaking as
        // `Uncaught (in promise)`.
        const result = cornerstone.utilities.jumpToSlice(viewportRef.current, {
          imageIndex: currentSlice,
          volumeId: volumeIdRef.current,
        });
        if (result && typeof (result as PromiseLike<void>).then === "function") {
          Promise.resolve(result).catch((err: unknown) => {
            logger.warn("[VolumeViewport] jumpToSlice (currentSlice) failed:", err);
          });
        }
      }
      // `lastNavigatedSliceRef` is owned by the CORNERSTONE_VOLUME_NEW_IMAGE
      // listener, which fires synchronously inside jumpToWorld/jumpToSlice and
      // writes the actual landed list-index. Overwriting it here with the
      // requested value re-opens the #468 oscillation on reversed-order
      // reconstructed planes: the listener sets ref=98 (actual), then this
      // line resets ref=97 (requested), then the next render's effect sees
      // externalSliceIndex=98 (from onSliceChange propagation) vs ref=97 and
      // fires another jumpToWorld, looping forever.
      // mode=index = per-instance IPP missing from canonical metadata, so we
      // fell back to Cornerstone's K-step. Warn (not info) so unbackfilled
      // cohorts with patchy IPP show up in ops.
      const mode = ipp ? "world" : "index";
      const log = ipp ? logger.info : logger.warn;
      log("viewer_slice_jump seriesId=%s sliceIndex=%d mode=%s", seriesId, currentSlice, mode);
    } catch (err) {
      // Upgraded from logger.debug → logger.warn for parity with the async
      // .catch above. Sync throws here are real bugs, not hot-path noise;
      // logger.debug is a no-op in prod (silent guard).
      logger.warn("[VolumeViewport] jumpToSlice (currentSlice) failed:", err);
    }
    // seriesId is a dep so a series swap re-runs the jump for the same
    // currentSlice against the new series's imageIds (read from the ref) and
    // its Cornerstone-held IPP.
  }, [currentSlice, useGlobalSliceState, totalSlices, seriesId]);

  // Cross-viewport AI jump: aiJumpTargetAtom carries the world-space IPP of
  // the most recent AI-finding click. Every volume viewport listens and
  // snaps its camera to that world point via jumpToWorld — direction-
  // agnostic, orientation-agnostic. The atom's `trigger` field is the
  // useEffect anchor so repeat clicks on the same finding re-fire.
  //
  // Atom + trigger cursor (intentional exception to viewerBus — see
  // CLAUDE.md "atom-vs-bus" entry): the effect's deps include `seriesId`
  // and `studyMetadata` so a series swap into this slot fires the
  // outstanding target for the newly-loaded volume. Without
  // a per-fire cursor, any unrelated dep change (a studyMetadata refetch,
  // for instance) would re-run jumpToWorld with the stale target and snap
  // the camera back, fighting the rad's manual scroll. The ref tracks which
  // trigger we've applied; an effect run with the same trigger is a no-op.
  // Cleared on seriesId change so a fresh series still picks up the target.
  const aiJumpTarget = useAtomValue(aiJumpTargetAtom);
  const lastFiredAiJumpTriggerRef = useRef<number | null>(null);
  useEffect(() => {
    lastFiredAiJumpTriggerRef.current = null;
  }, [seriesId]);
  useEffect(() => {
    if (!aiJumpTarget) return;
    if (lastFiredAiJumpTriggerRef.current === aiJumpTarget.trigger) return;
    // Consume-on-decision policy (not consume-up-front): mark the trigger
    // applied at each terminal site below — FoR-bail OR jumpToWorld
    // success. A jumpToWorld throw (no volume actor yet — volume still
    // binding) leaves the ref untouched so the bind-site post-bind sync
    // can pick up the outstanding target. Pre-refactor this was marked
    // up-front, which lost AI targets that fired during the bind window.
    // Plane gate (see checkAiJumpForGate): an AI finding has only a slice index,
    // so it jumps its OWN series and no other plane. A cross-series viewport
    // (a different plane, or a comparison/prior study sharing this atom) bails
    // — without an in-slice point a cross-plane jumpToWorld snaps to a
    // meaningless FOV-edge slice. Log at warn on bail so it surfaces in ops.
    const gate = checkAiJumpForGate(aiJumpTarget, seriesId);
    if (!gate.ok) {
      logAiJumpBail(gate.reason, "effect", aiJumpTarget, seriesId);
      lastFiredAiJumpTriggerRef.current = aiJumpTarget.trigger;
      return;
    }
    const csViewport = lookupCornerstoneViewport(renderingEngineRef.current, viewportIdRef.current);
    if (!csViewport || typeof csViewport.jumpToWorld !== "function") return;
    try {
      // Aim in the volume's REAL geometry frame — the sidecar ipp is
      // synthesized (z·normal) on canonical studies and lands slices away on
      // gantry-tilted series (#921). Gate is same-series here, so the pane's
      // own imageIds are the target's list.
      csViewport.jumpToWorld(
        resolveAiJumpWorld(aiJumpTarget, getCornerstone().metaData, imageIdsRef.current ?? [])
          .world,
      );
      // `lastNavigatedSliceRef` is owned by the CORNERSTONE_VOLUME_NEW_IMAGE
      // listener (sync inside jumpToWorld), which writes the actual landed
      // list-index. Writing `aiJumpTarget.sliceIndex` here would clobber the
      // listener's value on reversed-order reconstructed planes — see #468.
      lastFiredAiJumpTriggerRef.current = aiJumpTarget.trigger;
      logger.info(
        "ai_slice_jump_cross seriesId=%s targetSeriesId=%s targetSliceIndex=%d trigger=%d",
        seriesId,
        aiJumpTarget.seriesId,
        aiJumpTarget.sliceIndex,
        aiJumpTarget.trigger,
      );
    } catch (err) {
      // Throw before bind site has had a chance to sync; leave the
      // trigger ref untouched so the bind-site post-bind sync (which
      // reads the same atom + ref) can apply this target when the
      // viewport finishes binding.
      logger.warn(
        "ai_slice_jump_cross jumpToWorld failed seriesId=%s targetSeriesId=%s err=%s",
        seriesId,
        aiJumpTarget.seriesId,
        err instanceof Error ? err.message : String(err),
      );
    }
    // Re-fire when the target trigger changes; a series swap changes seriesId
    // so a viewport that just got its volume re-evaluates the outstanding
    // target. (No longer reads studyMetadata — the plane gate is series-only.)
  }, [aiJumpTarget, seriesId]);

  // Navigate to slice when externalSliceIndex prop changes (for hanging protocol sync)
  useEffect(() => {
    if (externalSliceIndex === undefined) return;
    // No volume-bound gate — see companion currentSlice effect.
    if (!volumeIdRef.current) return;
    if (!viewportRef.current) return;
    if (externalSliceIndex === lastNavigatedSliceRef.current) return; // Already at this slice

    // Bounds check - prevent navigation to invalid slices
    // Use localTotalSlices for hanging protocol viewports
    const sliceCount = useGlobalSliceState ? totalSlices : localTotalSlices;
    if (sliceCount > 0 && (externalSliceIndex < 0 || externalSliceIndex >= sliceCount)) {
      return;
    }

    const cornerstone = getCornerstone();
    const csViewport = lookupCornerstoneViewport(renderingEngineRef.current, viewportIdRef.current);

    try {
      // Real per-imageId IPP from Cornerstone's metaData — see the companion
      // currentSlice effect above for the longer rationale (why not
      // studyMetadata / z_position / jumpToSlice index).
      const ipp = sliceWorldPositionFromMetadata(
        cornerstone.metaData,
        (imageIdsRef.current ?? [])[externalSliceIndex],
      );
      if (ipp && typeof csViewport?.jumpToWorld === "function") {
        csViewport.jumpToWorld(ipp);
      } else {
        // utilities.jumpToSlice is async — same Uncaught (in promise) leak
        // as the companion currentSlice effect above. Hanging-protocol slot
        // updates and AI-jump fan-out trigger this path frequently.
        const result = cornerstone.utilities.jumpToSlice(viewportRef.current, {
          imageIndex: externalSliceIndex,
          volumeId: volumeIdRef.current,
        });
        if (result && typeof (result as PromiseLike<void>).then === "function") {
          Promise.resolve(result).catch((err: unknown) => {
            logger.warn("[VolumeViewport] jumpToSlice (externalSliceIndex) failed:", err);
          });
        }
      }
      // `lastNavigatedSliceRef` is owned by the CORNERSTONE_VOLUME_NEW_IMAGE
      // listener (sync inside jumpToWorld/jumpToSlice). Writing
      // externalSliceIndex here is the #468 oscillation root cause on
      // reversed-order reconstructed planes — see the companion currentSlice
      // effect for the trace.
      // setLocalCurrentSlice is also written by the listener with the actual
      // landed list-index; mirroring externalSliceIndex here would clobber it.
      // See companion currentSlice effect for why mode=index is warn-level.
      const mode = ipp ? "world" : "index";
      const log = ipp ? logger.info : logger.warn;
      log(
        "viewer_slice_jump seriesId=%s sliceIndex=%d mode=%s",
        seriesId,
        externalSliceIndex,
        mode,
      );
    } catch (err) {
      // Upgraded from logger.debug → logger.warn for parity with the async
      // .catch above (see companion effect for rationale).
      logger.warn("[VolumeViewport] jumpToSlice (externalSliceIndex) failed:", err);
    }
  }, [externalSliceIndex, useGlobalSliceState, totalSlices, localTotalSlices, seriesId]);

  // Apply isInverted prop → Cornerstone setProperties({invert}). The slot's
  // toggleInvert atom flips state but only takes effect when this effect
  // pushes the value into the viewport.
  useEffect(() => {
    if (isInverted === undefined) return;
    if (!volumeIdRef.current) return;
    try {
      const cornerstone = getCornerstone();
      const renderingEngine = cornerstone.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
      const viewportId = viewportIdRef.current;
      if (!renderingEngine || !viewportId) return;
      const viewport = renderingEngine.getViewport(viewportId) as unknown as {
        setProperties: (props: { invert?: boolean }, volumeId?: string) => void;
      };
      viewport.setProperties({ invert: isInverted }, volumeIdRef.current);
      renderingEngine.render();
    } catch {
      // Cornerstone not initialized yet
    }
  }, [isInverted]);

  // Prevent page scroll when wheeling over viewport
  // Must use native event listener with { passive: false } - React's onWheel is passive
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // { passive: false } is required for preventDefault() to work
    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => element.removeEventListener("wheel", handleWheel);
  }, []);

  useSegmentationCursor(viewportRef, activeMeasurementTool, bumpRadius);
  useMeasureClickWiring(viewportRef, seriesId);

  // Plane-color corner bracket (axial=blue, coronal=green, sagittal=red).
  // Mirrors PLANE_COLORS_HEX used by the 3D viewer + legend so a viewer can
  // tell at a glance which orientation each viewport is rendering.
  const planeColor = useMemo(() => {
    const meta = studyMetadata?.series_metadata?.[seriesId];
    if (!meta) return null;
    const plane = derivePlane(meta);
    if (plane === "Axial") return PLANE_COLORS_HEX.axial;
    if (plane === "Coronal") return PLANE_COLORS_HEX.coronal;
    if (plane === "Sagittal") return PLANE_COLORS_HEX.sagittal;
    return null;
  }, [studyMetadata, seriesId]);

  // nnInteractive in-flight spinner. The runtime sets both the primary
  // viewportId and any companion viewport IDs so the spinner appears on every
  // viewport whose contours are still pending.
  const nnLoading = useAtomValue(nnInteractiveLoadingAtom);
  const thisViewportId = viewportIdRef.current ?? viewportIdOverride ?? null;
  const showNnSpinner =
    nnLoading.isLoading &&
    thisViewportId !== null &&
    (nnLoading.viewportId === thisViewportId ||
      (nnLoading.companionViewportIds ?? []).includes(thisViewportId));

  // Conditional return must come AFTER all hooks above to honor React's
  // rules-of-hooks (hook count must be stable across renders). Earlier this
  // sat above useSegmentationCursor/useMeasureClickWiring/useMemo/useAtomValue,
  // and flipping `error` mid-render produced "Rendered fewer hooks than
  // expected" crashes on series-swap.
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-bg-2 text-warn">
        <div className="text-center">
          <p className="text-lg font-semibold">Error</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-black">
      <div ref={viewportRef} data-measure-target="true" className="w-full h-full" />
      <SegmentationBadge activeTool={activeMeasurementTool} />
      {planeColor && (
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-5 h-5 pointer-events-none"
          style={{
            borderLeft: `4px solid ${planeColor}`,
            borderBottom: `4px solid ${planeColor}`,
          }}
        />
      )}
      {showNnSpinner && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-[1px] pointer-events-none"
          data-testid="nn-interactive-spinner"
        >
          <div className="h-10 w-10 rounded-full border-4 border-signal border-t-transparent animate-spin" />
          <div className="text-sm text-white/90 font-medium tracking-wide">
            Running AI segmentation…
          </div>
        </div>
      )}
    </div>
  );
};

export default VolumeViewport;
